/**
 * xyzSerializer — turn the live scene's elements + positions back into XYZ
 * text. The inverse of the geometry half of {@link parseXYZ}.
 *
 * Used to persist a hand-built sandbox structure into the `?molxyz=` URL
 * param (see {@link installSandboxUrlSync}), so a build is shareable /
 * restorable on reload. Geometry only — velocities/cell/dynamics/visual flags
 * are intentionally not emitted (the URL should stay a compact snapshot of
 * "what atoms are where").
 *
 * @module loader/xyzSerializer
 */

interface Vec3Like {
  x: number;
  y: number;
  z: number;
}

/** ArcRotateCamera pose to embed in the comment line. */
export interface CameraViewLike {
  alpha: number;
  beta: number;
  radius: number;
  target: Vec3Like;
}

/** A pairwise bias ("tractor beam") as index pairs + signed strength. */
export interface BiasPair {
  indexA: number;
  indexB: number;
  strength: number;
}

export interface SerializeXYZOptions {
  /** Comment line prefix (line 2). Defaults to `'MLIPView sandbox'`. */
  comment?: string;
  /** Camera pose to embed as `cam_alpha=…` tokens on the comment line. */
  camera?: CameraViewLike | null;
  /** Tractor-beam biases to embed as a `biases=iA:iB:s,…` token. */
  biases?: ReadonlyArray<BiasPair> | null;
}

function fmt(v: number): string {
  return Number.isFinite(v) ? v.toFixed(6) : '0.000000';
}

/** Append a `biases=iA:iB:s,…` token when there are (finite) biases to embed. */
function commentWithBiases(comment: string, biases?: ReadonlyArray<BiasPair> | null): string {
  if (!biases || biases.length === 0) return comment;
  const parts: string[] = [];
  for (const b of biases) {
    if (!Number.isInteger(b.indexA) || !Number.isInteger(b.indexB)) continue;
    if (b.indexA < 0 || b.indexB < 0) continue;
    if (!Number.isFinite(b.strength) || b.strength === 0) continue;
    parts.push(`${b.indexA}:${b.indexB}:${b.strength.toFixed(4)}`);
  }
  return parts.length ? `${comment} biases=${parts.join(',')}` : comment;
}

/**
 * Read active pairwise biases off a MoleculeState as index-keyed {@link BiasPair}s
 * (converting the hash-keyed live set via `hashToIndex`), ready to embed for
 * export. Returns `null` when there are none. Loosely typed so callers can pass
 * `ctx.moleculeState` without a hard dependency.
 */
export function readBiasesForExport(molState: unknown): BiasPair[] | null {
  const m = molState as {
    getBiases?: () => ReadonlyArray<{ hashA: number; hashB: number; strength: number }>;
    activeBiases?: ReadonlyArray<{ hashA: number; hashB: number; strength: number }>;
    hashToIndex?: (hash: number) => number;
  } | null | undefined;
  const list = m?.getBiases?.() ?? m?.activeBiases;
  const h2i = m?.hashToIndex;
  if (!Array.isArray(list) || list.length === 0 || typeof h2i !== 'function') return null;
  const out: BiasPair[] = [];
  for (const b of list) {
    const indexA = h2i(b.hashA);
    const indexB = h2i(b.hashB);
    if (indexA >= 0 && indexB >= 0 && Number.isFinite(b.strength) && b.strength !== 0) {
      out.push({ indexA, indexB, strength: b.strength });
    }
  }
  return out.length ? out : null;
}

/**
 * Extract a serializable {@link CameraViewLike} from a Babylon ArcRotateCamera
 * (or anything with `alpha`/`beta`/`radius`/`target`). Returns `null` when the
 * pose isn't fully numeric, so callers can pass the result straight to
 * `serializeXYZ` without extra guards.
 */
export function readCameraView(camera: unknown): CameraViewLike | null {
  const c = camera as
    | { alpha?: unknown; beta?: unknown; radius?: unknown; target?: { x?: unknown; y?: unknown; z?: unknown } }
    | null
    | undefined;
  if (!c) return null;
  const { alpha, beta, radius, target } = c;
  if (typeof alpha !== 'number' || typeof beta !== 'number' || typeof radius !== 'number') return null;
  if (!target || typeof target.x !== 'number' || typeof target.y !== 'number' || typeof target.z !== 'number') {
    return null;
  }
  return { alpha, beta, radius, target: { x: target.x, y: target.y, z: target.z } };
}

/** Append `cam_*` tokens to the comment when a (finite) camera pose is given. */
function commentWithCamera(comment: string, camera?: CameraViewLike | null): string {
  if (!camera) return comment;
  const { alpha, beta, radius, target } = camera;
  const nums = [alpha, beta, radius, target?.x, target?.y, target?.z];
  if (!nums.every((v) => typeof v === 'number' && Number.isFinite(v))) return comment;
  const tgt = `${fmt(target.x)},${fmt(target.y)},${fmt(target.z)}`;
  return `${comment} cam_alpha=${fmt(alpha)} cam_beta=${fmt(beta)} cam_radius=${fmt(radius)} cam_target=${tgt}`;
}

/**
 * Serialize parallel `elements` / `positions` arrays into standard XYZ text:
 *
 *   N
 *   <comment>
 *   El x y z
 *   ...
 *
 * Trailing newline included. Positions beyond `elements.length` are ignored;
 * missing positions default to the origin. When `options.camera` is given, the
 * camera pose is embedded on the comment line as `cam_alpha=/cam_beta=/
 * cam_radius=/cam_target=` tokens (round-tripped by {@link parseXYZ}), so a
 * reload restores the view. The atom lines are unaffected — the file stays a
 * valid `.xyz` for any other tool.
 */
export function serializeXYZ(
  elements: ReadonlyArray<string>,
  positions: ReadonlyArray<Vec3Like>,
  options: SerializeXYZOptions = {},
): string {
  const baseComment = options.comment ?? 'MLIPView sandbox';
  const comment = commentWithBiases(commentWithCamera(baseComment, options.camera), options.biases);
  const n = elements.length;
  const lines: string[] = [String(n), comment];
  for (let i = 0; i < n; i++) {
    const p = positions[i] ?? { x: 0, y: 0, z: 0 };
    lines.push(`${elements[i]} ${fmt(p.x)} ${fmt(p.y)} ${fmt(p.z)}`);
  }
  return lines.join('\n') + '\n';
}
